import React, { ComponentProps, HTMLAttributes } from 'react';
import { styled } from 'styled-components';

import FeaturedReview from '@rover/components/src/cards/SearchResultCard/FeaturedReview';
import ImageColumn from '@rover/components/src/cards/SearchResultCard/ImageColumn';
import LegacyPriceColumn from '@rover/components/src/cards/SearchResultCard/PriceColumn/LegacyPriceColumn';
import {
  leftBoxShadow,
  MemberProfileAnchor,
  SearchResultCardWrapper,
} from '@rover/components/src/cards/SearchResultCard/SearchResultCard';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { HorizontalLayout } from '@rover/react-lib/src/components/layouts';
import { shouldLazyLoadImage } from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import { TrainerSearchResult } from '@rover/rsdk/src/apiClient/latest/model/trainerSearchResult';
import {
  FLAGS,
  useFrontendConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

import useDeviceContext from '../../../../../context/useDeviceContext';

import TrainerInfoColumn from './TrainerInfoColumn';
import TrainerPills from './TrainerPills';

export interface TrainerSearchCardWrapperProps
  extends ComponentProps<typeof SearchResultCardWrapper> {
  onClick?: HTMLAttributes<HTMLDivElement>['onClick'];
  tabIndex?: HTMLAttributes<HTMLDivElement>['tabIndex'];
}
export const TrainerSearchCardWrapper = styled(
  SearchResultCardWrapper
)<TrainerSearchCardWrapperProps>`
  cursor: pointer;
  gap: 16px;

  &:active {
    ${({ theme }) => leftBoxShadow(theme)};
  }
`;

interface TrainerSearchCardProps {
  searchResult: TrainerSearchResult;
  isMapExpanded?: boolean;
  index: number;
  currencyCode?: string;
  locale?: Locale;
  shouldUsePriceTransparency?: boolean;
  href?: string;
}

export default function TrainerSearchCard({
  searchResult,
  isMapExpanded = false,
  index,
  // TODO (DEV-104195): Maybe out-of-scope, but if not, pass these props through from the page
  currencyCode = 'USD',
  locale = Locale.EnUs,
  shouldUsePriceTransparency = false,
  href,
}: TrainerSearchCardProps): JSX.Element {
  const {
    defaultImage: { small: smallImage, medium: mediumImage },
    shortName,
    primaryBadgeData,
    price,
    fullPrice,
    unitAsSentence,
    trainerWebUrl,
    city,
    state,
    title,
    ratingsAverage,
    reviewsCount,
    yearsOfExperience,
    reviewText,
    trainerExperienceDetails,
    reviewDogPhotoUrl,
    virtualOnly,
    repeatTrainingClientCount,
    allServiceRepeatClientCount,
    flair,
  } = searchResult;

  const { isDesktop } = useDeviceContext();
  const { isFlagActive } = useFrontendConfiguration();
  const cardTreatment = SearchCardTreatment.legacy;

  const isAllServicesClientCountActive = isFlagActive(
    FLAGS.ROLLOUT_TRAINING_ALL_SERVICE_CLIENT_COUNT
  );

  const repeatClientCount = isAllServicesClientCountActive
    ? allServiceRepeatClientCount
    : repeatTrainingClientCount;

  return (
    <TrainerSearchCardWrapper
      data-qa-id="trainer-search-results"
      data-testid="trainer-search-result-card"
    >
      <MemberProfileAnchor
        aria-label={`Search result number ${index + 1}: ${shortName}`}
        href={href ?? trainerWebUrl}
        target={isDesktop ? '_blank' : '_self'}
      />
      <HorizontalLayout noWrap>
        <ImageColumn
          displayExpanded={!isMapExpanded}
          desktopProfileImage={mediumImage}
          mobileProfileImage={smallImage}
          shortName={shortName}
          lazy={shouldLazyLoadImage(index)}
          badge={primaryBadgeData}
          cardTreatment={cardTreatment}
        />
        <TrainerInfoColumn
          displayExpanded={!isMapExpanded}
          city={city}
          state={state}
          title={title}
          shortName={shortName}
          flair={flair}
          virtualOnly={virtualOnly}
        />
        <LegacyPriceColumn
          currencyCode={currencyCode}
          locale={locale}
          price={shouldUsePriceTransparency && fullPrice !== null ? fullPrice : price}
          priceTransparencyLevel={
            shouldUsePriceTransparency ? PriceTransparencyLevels.full : PriceTransparencyLevels.none
          }
          unitAsSentence={unitAsSentence}
          isTrainingDesign
        />
      </HorizontalLayout>
      <TrainerPills
        ratingsAverage={ratingsAverage}
        reviewsCount={reviewsCount}
        yearsOfExperience={yearsOfExperience}
        repeatClientCount={repeatClientCount}
        isAllServicesClientCountActive={isAllServicesClientCountActive}
      />
      <FeaturedReview
        description={trainerExperienceDetails}
        reviewText={reviewText}
        reviewPetPhotoUrl={reviewDogPhotoUrl}
      />
    </TrainerSearchCardWrapper>
  );
}
